import { useAuiState } from '@assistant-ui/react'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useLayoutEffect, useRef, useState } from 'react'
import { schemaSessionKeys } from './use-schema-session'

/** A turn's end is not the end of the wait: keep activity visible until the
 * session refresh lands, or offer a retry if the saved review cannot be loaded. */
export function useSchemaChatSync(caseId: string) {
  const running = useAuiState(state => state.thread.isRunning)
  const previous = useRef({ caseId, running: false })
  const request = useRef(0)
  const [state, setState] = useState<'idle' | 'refreshing' | 'error'>('idle')
  const qc = useQueryClient()
  const refresh = useCallback(async () => {
    const id = ++request.current
    setState('refreshing')
    try {
      await qc.invalidateQueries(
        { queryKey: schemaSessionKeys.current(caseId) },
        { throwOnError: true }
      )
      if (id === request.current) setState('idle')
    } catch {
      if (id === request.current) setState('error')
    }
  }, [qc, caseId])

  // Hand off to refresh before paint, without blinking idle or enabling Send.
  useLayoutEffect(() => {
    const finished = previous.current.caseId === caseId && previous.current.running && !running
    previous.current = { caseId, running }
    if (finished) void refresh()
    else setState('idle')
    return () => {
      // Ignore stale refreshes after a new turn, case switch or unmount.
      request.current++
    }
  }, [running, refresh, caseId])

  return {
    settling: state === 'refreshing',
    error: state === 'error',
    retry: () => void refresh(),
  }
}
